import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { FaChartLine, FaCheck } from 'react-icons/fa';
import api from '../services/api';

const RelatorioGestao = () => {
  const [relatorios, setRelatorios] = useState([]);
  const [relatorioSelecionado, setRelatorioSelecionado] = useState(null);
  const [semestreFiltro, setSemestreFiltro] = useState('1º');
  const [anoFiltro, setAnoFiltro] = useState('2026');
  const [isMobile, setIsMobile] = useState(false);

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const styles = getStyles(isMobile);

  // Carregar relatórios da API
  const carregarRelatorios = async () => {
    try {
      const response = await api.get('/reports');
      const dados = response.data.data.reports || [];
      setRelatorios(dados);
      if (dados.length > 0) {
        const ultimo = dados[dados.length - 1];
        setSemestreFiltro(ultimo.semestre || ultimo.trimestre);
        setAnoFiltro(ultimo.ano);
      } else {
        setRelatorioSelecionado(null);
      }
    } catch (err) {
      console.error('Erro ao carregar relatórios:', err);
      setRelatorios([]);
      setRelatorioSelecionado(null);
    }
  };

  useEffect(() => {
    carregarRelatorios();
  }, []);

  useEffect(() => {
    if (relatorios.length > 0) {
      const encontrado = relatorios.find(
        r => (r.semestre || r.trimestre) === semestreFiltro && r.ano === anoFiltro
      );
      setRelatorioSelecionado(encontrado || null);
    } else {
      setRelatorioSelecionado(null);
    }
  }, [relatorios, semestreFiltro, anoFiltro]);

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const obterAnosDisponiveis = () => {
    const anos = relatorios.map(r => r.ano);
    return [...new Set(anos)].sort((a, b) => b - a);
  };

  const obterSemestresDisponiveis = () => {
    const semestres = relatorios
      .filter(r => r.ano === anoFiltro)
      .map(r => r.semestre || r.trimestre);
    return [...new Set(semestres)].sort();
  };

  return (
    <Layout title="Relatório de Gestão" icon={FaChartLine}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.logoArea}>
            <h1 style={styles.title}>Relatório de Gestão e Avanço</h1>
            <p style={styles.slogan}>Transparência, melhoria e resultados para você</p>
          </div>
          <div style={styles.filtros}>
            <div style={styles.filtroGroup}>
              <label style={styles.label}>Semestre:</label>
              <select
                style={styles.select}
                value={semestreFiltro}
                onChange={(e) => setSemestreFiltro(e.target.value)}
              >
                {obterSemestresDisponiveis().map(sem => (
                  <option key={sem} value={sem}>{sem} Semestre</option>
                ))}
              </select>
            </div>
            <div style={styles.filtroGroup}>
              <label style={styles.label}>Ano:</label>
              <select
                style={styles.select}
                value={anoFiltro}
                onChange={(e) => setAnoFiltro(e.target.value)}
              >
                {obterAnosDisponiveis().map(ano => (
                  <option key={ano} value={ano}>{ano}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div style={styles.content}>
          {relatorioSelecionado ? (
            <>
              {/* Retângulo azul claro atrás do título do período */}
              <div style={{ ...styles.retanguloAzul, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <img
                    src="/logo.png"
                    alt="Logo Olympic Club"
                    style={{ width: 80, height: 80, objectFit: 'contain' }}
                  />
                  <div>
                    <h2 style={styles.periodoTitle}>RELATÓRIO SEMESTRAL</h2>
                    <p style={styles.periodo}>
                      {relatorioSelecionado.semestre || relatorioSelecionado.trimestre} semestre - {relatorioSelecionado.ano}
                    </p>
                  </div>
                </div>
              </div>

              <div style={styles.secoesGrid}>
                {/* Melhorias */}
                <div style={{ ...styles.cardSecao, borderTop: '4px solid #2e7d32' }}>
                  <h3 style={styles.cardTitle}>✨ Melhorias Realizadas</h3>
                  <ul style={styles.lista}>
                    {relatorioSelecionado.melhorias.map((item, idx) => (
                      <li key={idx} style={{ ...styles.listItem, ...styles.itemMelhoria }}>
                        <FaCheck style={{ ...styles.checkIcon, color: '#2e7d32' }} /> {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Manutenções */}
                <div style={{ ...styles.cardSecao, borderTop: '4px solid #e65100' }}>
                  <h3 style={styles.cardTitle}>🛠️ Manutenções Executadas</h3>
                  <ul style={styles.lista}>
                    {relatorioSelecionado.manutencoes.map((item, idx) => (
                      <li key={idx} style={{ ...styles.listItem, ...styles.itemManutencao }}>
                        <FaCheck style={{ ...styles.checkIcon, color: '#e65100' }} /> {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Investimentos com Total Investido */}
                <div style={{ ...styles.cardSecao, borderTop: '4px solid #7b1fa2' }}>
                  <h3 style={styles.cardTitle}>💰 Investimentos</h3>
                  <ul style={styles.lista}>
                    {relatorioSelecionado.investimentos.map((item, idx) => (
                      <li key={idx} style={{ ...styles.listItem, ...styles.itemInvestimento }}>
                        <FaCheck style={{ ...styles.checkIcon, color: '#7b1fa2' }} /> {item}
                      </li>
                    ))}
                  </ul>
                  {relatorioSelecionado.valorTotal > 0 && (
                    <div style={styles.totalInvestidoSimples}>
                      <strong>Total Investido:</strong> {formatarMoeda(relatorioSelecionado.valorTotal)}
                    </div>
                  )}
                </div>

                {/* Próximas Ações */}
                <div style={{ ...styles.cardSecao, borderTop: '4px solid #1565c0' }}>
                  <h3 style={styles.cardTitle}>🎯 Próximas Ações</h3>
                  <ul style={styles.lista}>
                    {relatorioSelecionado.proximasAcoes.map((item, idx) => (
                      <li key={idx} style={{ ...styles.listItem, ...styles.itemAcao }}>
                        <FaCheck style={{ ...styles.checkIcon, color: '#1565c0' }} /> {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Mensagem */}
                <div style={styles.cardSecao}>
                  <h3 style={styles.cardTitle}>📄 Mensagem da Diretoria</h3>
                  <p style={styles.mensagem}>{relatorioSelecionado.mensagem}</p>
                </div>
              </div>
            </>
          ) : (
            <div style={styles.semDados}>
              <p>Nenhum relatório encontrado para o período selecionado.</p>
              <p>Utilize a página de administração para criar novos relatórios.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

// Estilos - Responsivo: 2 colunas no desktop, 1 coluna no mobile
const getStyles = (isMobile) => {
  return {
    container: {
      padding: '20px',
      fontFamily: '"Montserrat", "Arial", sans-serif',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: '30px',
      paddingBottom: '20px',
      borderBottom: '3px solid #2c3e50',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '20px' : '0',
    },
    logoArea: {
      flex: 1,
      textAlign: isMobile ? 'center' : 'left',
    },
    title: {
      margin: 0,
      fontSize: isMobile ? '1.4rem' : '1.8rem',
      color: '#2c3e50',
      marginBottom: '10px',
    },
    slogan: {
      margin: 0,
      fontSize: isMobile ? '1rem' : '1.2rem',
      color: '#0056b3',
      backgroundColor: '#f0f0f0',
      border: '2px solid #000000',
      padding: '10px 15px',
      borderRadius: '8px',
      display: 'inline-block',
      fontWeight: 'bold',
      marginBottom: '20px',
    },
    filtros: {
      display: 'flex',
      gap: '15px',
      backgroundColor: '#f9f9f9',
      padding: '10px 20px',
      borderRadius: '10px',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    filtroGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    label: {
      fontWeight: 'bold',
      color: '#2c3e50',
      fontSize: '0.85rem',
    },
    select: {
      padding: '5px 10px',
      borderRadius: '5px',
      border: '1px solid #ddd',
      backgroundColor: '#fff',
      fontSize: '0.85rem',
      cursor: 'pointer',
    },
    content: {
      marginTop: '20px',
    },
    retanguloAzul: {
      backgroundColor: '#e3f2fd',
      padding: '20px',
      borderRadius: '12px',
      textAlign: 'center',
      marginBottom: '30px',
      border: '1px solid #bbdefb',
    },
    periodoTitle: {
      fontSize: isMobile ? '1.2rem' : '1.6rem',
      color: '#1565c0',
      margin: 0,
      fontWeight: '800',
      textTransform: 'uppercase',
    },
    periodo: {
      fontSize: isMobile ? '0.9rem' : '1.1rem',
      color: '#546e7a',
      margin: 0,
      marginTop: '5px',
    },
    secoesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px',
      marginBottom: '40px',
    },
    cardSecao: {
      backgroundColor: '#fff',
      borderRadius: '10px',
      padding: '20px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
      border: '1px solid #e0e0e0',
    },
    cardTitle: {
      margin: 0,
      marginBottom: '20px',
      fontSize: isMobile ? '1.2rem' : '1.4rem',
      color: '#2c3e50',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    lista: {
      margin: 0,
      padding: 0,
      listStyleType: 'none',
    },
    listItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      marginBottom: '12px',
      fontSize: '0.95rem',
      color: '#333',
      lineHeight: '1.4',
      padding: '8px 10px',
      borderRadius: '8px',
    },
    itemMelhoria: {
      backgroundColor: '#e8f5e9',
      borderLeft: '4px solid #2e7d32',
    },
    itemManutencao: {
      backgroundColor: '#fff3e0',
      borderLeft: '4px solid #e65100',
    },
    itemInvestimento: {
      backgroundColor: '#f3e5f5',
      borderLeft: '4px solid #7b1fa2',
    },
    itemAcao: {
      backgroundColor: '#e3f2fd',
      borderLeft: '4px solid #1565c0',
    },
    checkIcon: {
      color: '#28a745',
      fontSize: '1.1rem',
      marginTop: '2px',
      flexShrink: 0,
    },
    totalInvestidoSimples: {
      marginTop: '15px',
      paddingTop: '15px',
      borderTop: '1px solid #e0e0e0',
      fontSize: '1rem',
      color: '#555',
      textAlign: 'right',
    },
    mensagem: {
      fontSize: '0.95rem',
      color: '#555',
      fontStyle: 'italic',
      lineHeight: '1.6',
      margin: 0,
      padding: '15px',
      backgroundColor: '#fffde7',
      borderRadius: '8px',
      borderLeft: '5px solid #fbc02d',
    },
    semDados: {
      textAlign: 'center',
      padding: '40px',
      backgroundColor: '#f9f9f9',
      borderRadius: '10px',
      color: '#7f8c8d',
    },
  };
};

export default RelatorioGestao;